import React, { useEffect, useState, useCallback } from "react";
import api from "../api";
import toast from 'react-hot-toast';
import MainLayout from "./MainLayout";
import TopNavBar from "./TopNavBar";
import { useAuth } from "../context/AuthContext";
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX } from "react-icons/fi";
import { Select } from "antd";
import "../styles/master.css";
import LeavePolicyPage from "./LeavePolicyPage";
import LeaveType from "./LeaveTypes";
import HolidaysList from "./HolidaysPage";
import ReportSettings from "./ReportSettings";
import OfficeLocations from "./OfficeLocations";
import CheckinSettings from "./CheckinSettings";
/* ══════════════════════════════════════════════
   MODAL
══════════════════════════════════════════════ */
function Modal({ title, onClose, children }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   GENERIC MASTER PANEL  (Dept / Designation / Status / Priority)
   idField  = the unique code field (e.g. department_id)
   nameField= the display name field (e.g. department_name)
══════════════════════════════════════════════ */
function MasterPanel({ apiPath, idField, nameField, label, accentColor, idPlaceholder, namePlaceholder }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);   // null | create | edit | delete
  const [current, setCurrent] = useState(null);
  const [formId, setFormId] = useState("");
  const [formName, setFormName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath);
      setRows(res.data.data || []);
    } catch { toast.error(`Failed to load ${label}`); }
    finally { setLoading(false); }
  }, [apiPath, label]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(
    (r) =>
      r[idField]?.toLowerCase().includes(search.toLowerCase()) ||
      r[nameField]?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setFormId(""); setFormName(""); setModal("create"); };
  const openEdit = (row) => { setCurrent(row); setFormId(row[idField]); setFormName(row[nameField]); setModal("edit"); };
  const openDelete = (row) => { setCurrent(row); setModal("delete"); };
  const closeModal = () => { setModal(null); setCurrent(null); };

  const handleCreate = async () => {
    if (!formId.trim()) return toast.error(`${label} Code is required`);
    if (!formName.trim()) return toast.error(`${label} Name is required`);
    setSaving(true);
    try {
      await api.post(apiPath, { [idField]: formId, [nameField]: formName });
      toast.success(`${label} created`);
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to create"); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!formName.trim()) return toast.error(`${label} Name is required`);
    setSaving(true);
    try {
      await api.put(`${apiPath}/${current[idField]}`, { [nameField]: formName });
      toast.success(`${label} updated`);
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`${apiPath}/${current[idField]}`);
      toast.success(`${label} deleted`);
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to delete"); }
    finally { setSaving(false); }
  };

  return (
    <div className="tab-panel">
      {/* Panel header */}
      <div className="panel-header">
        <div className="panel-header-left">
          <div className="panel-title">{label}</div>
          <div className="panel-sub">Manage and configure {label.toLowerCase()} settings</div>
        </div>
        <div className="panel-header-right">
          <div className="search-wrap">
            <FiSearch size={15} color="#94a3b8" />
            <input
              className="search-input"
              placeholder={`Search by name, code...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            className="btn-add-primary"
            style={{ "--acc": accentColor }}
            onClick={openCreate}
          >
            <FiPlus size={15} /> Add {label}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        {loading ? (
          <div className="empty-msg">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-msg">No {label.toLowerCase()}s found. Click <strong>Add {label}</strong> to get started.</div>
        ) : (
          <table className="master-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row[idField]}>
                  <td>
                    <span className="id-badge" style={{ "--accent": accentColor }}>
                      {row[idField]}
                    </span>
                  </td>
                  <td className="name-cell">{row[nameField]}</td>
                  <td>
                    <div className="action-btns">
                      <button className="btn-icon edit" title="Edit" onClick={() => openEdit(row)}>
                        <FiEdit2 size={13} />
                      </button>
                      <button className="btn-icon del" title="Delete" onClick={() => openDelete(row)}>
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CREATE modal */}
      {modal === "create" && (
        <Modal title={`Add ${label}`} onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">{label} Code <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              className="form-input"
              placeholder={idPlaceholder || `e.g. CODE001`}
              value={formId}
              onChange={(e) => setFormId(e.target.value)}
            />
            <span className="form-hint">Auto-converted to UPPERCASE</span>
          </div>
          <div className="form-group">
            <label className="form-label">{label} Name <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              className="form-input"
              placeholder={namePlaceholder || `Enter ${label.toLowerCase()} name`}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button
              className="btn-primary"
              style={{ "--accent": accentColor }}
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? "Creating…" : "Create"}
            </button>
          </div>
        </Modal>
      )}

      {/* EDIT modal */}
      {modal === "edit" && (
        <Modal title={`Edit ${label}`} onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">{label} Code</label>
            <input className="form-input" value={formId} disabled style={{ opacity: 0.5 }} />
          </div>
          <div className="form-group">
            <label className="form-label">{label} Name <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              className="form-input"
              placeholder={namePlaceholder || `Enter ${label.toLowerCase()} name`}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button
              className="btn-primary"
              style={{ "--accent": accentColor }}
              onClick={handleEdit}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </Modal>
      )}

      {/* DELETE modal */}
      {modal === "delete" && (
        <Modal title={`Delete ${label}`} onClose={closeModal}>
          <p className="delete-msg">
            Are you sure you want to delete <strong>{current[idField]}</strong> — {current[nameField]}?
            This action cannot be undone.
          </p>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-danger" onClick={handleDelete} disabled={saving}>
              {saving ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   CLIENT MASTER PANEL  (special — has project dropdown)
══════════════════════════════════════════════ */
function ClientMasterPanel({ accentColor }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_id: "", client_name: "", project_id: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cm, pm] = await Promise.all([
        api.get("/api/client-master"),
        api.get("/api/projects?limit=500"),
      ]);
      setRows(cm.data.data || []);
      setProjects(pm.data.data?.projects || []);
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(
    (r) =>
      r.client_id?.toLowerCase().includes(search.toLowerCase()) ||
      r.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.project_name?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setForm({ client_id: "", client_name: "", project_id: [] }); setModal("create"); };
  const openEdit = (row) => {
    // project_id comes from backend as a comma separated string if there are multiple. 
    // We already changed it to texting in backend. Wait! `row.project_id` comes from backend...
    // The backend `getAll` returns `...cm` which includes project_id as string.
    const pIds = row.project_id ? (typeof row.project_id === 'string' ? row.project_id.split(',').map(id => isNaN(Number(id)) ? id : Number(id)) : []) : [];
    setCurrent(row);
    setForm({ client_id: row.client_id, client_name: row.client_name, project_id: pIds });
    setModal("edit");
  };
  const openDelete = (row) => { setCurrent(row); setModal("delete"); };
  const closeModal = () => { setModal(null); setCurrent(null); };

  const handleCreate = async () => {
    if (!form.client_id.trim()) return toast.error("Client ID is required");
    if (!form.client_name.trim()) return toast.error("Client Name is required");
    setSaving(true);
    try {
      await api.post("/api/client-master", form);
      toast.success("Client created successfully");
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to create"); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!form.client_name.trim()) return toast.error("Client Name is required");
    setSaving(true);
    try {
      await api.put(`/api/client-master/${current.id}`, { client_name: form.client_name, project_id: form.project_id?.length ? form.project_id : null });
      toast.success("Client updated successfully");
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/client-master/${current.id}`);
      toast.success("Client deleted");
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to delete"); }
    finally { setSaving(false); }
  };

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <div className="panel-header-left">
          <div className="panel-title">Client Master</div>
          <div className="panel-sub">Manage and configure client settings</div>
        </div>
        <div className="panel-header-right">
          <div className="search-wrap">
            <FiSearch size={15} color="#94a3b8" />
            <input className="search-input" placeholder="Search by client name..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn-add-primary" style={{ "--acc": accentColor }} onClick={openCreate}>
            <FiPlus size={15} /> Add Client
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty-msg">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-msg">No clients found. Click <strong>Add Client</strong> to get started.</div>
        ) : (
          <table className="master-table">
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Client Name</th>
                <th>Linked Project</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td><span className="id-badge" style={{ "--accent": accentColor }}>{row.client_id}</span></td>
                  <td className="name-cell">{row.client_name}</td>
                  <td>
                    {row.project_name
                      ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {row.project_name.split(',').map((name, i) => <span key={i} className="project-tag">{name.trim()}</span>)}
                      </div>
                      : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td>
                    <div className="action-btns">
                      <button className="btn-icon edit" title="Edit" onClick={() => openEdit(row)}><FiEdit2 size={13} /></button>
                      <button className="btn-icon del" title="Delete" onClick={() => openDelete(row)}><FiTrash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal === "create" && (
        <Modal title="Add Client" onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">Client ID <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="form-input" placeholder="e.g. CLT001" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Client Name <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="form-input" placeholder="Enter client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Linked Projects</label>
            <Select
              mode="multiple"
              allowClear
              placeholder="— Select Projects —"
              value={form.project_id}
              onChange={(value) => setForm({ ...form, project_id: value })}
              style={{ width: '100%' }}
              options={projects.map(p => ({ label: p.project_name || p.sfprojectsid, value: p.id }))}
            />
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" style={{ "--accent": accentColor }} onClick={handleCreate} disabled={saving}>{saving ? "Creating…" : "Create"}</button>
          </div>
        </Modal>
      )}

      {modal === "edit" && (
        <Modal title="Edit Client" onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">Client ID</label>
            <input className="form-input" value={form.client_id} disabled style={{ opacity: 0.5 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Client Name <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="form-input" placeholder="Enter client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Linked Projects</label>
            <Select
              mode="multiple"
              allowClear
              placeholder="— Select Projects —"
              value={form.project_id}
              onChange={(value) => setForm({ ...form, project_id: value })}
              style={{ width: '100%' }}
              options={projects.map(p => ({ label: p.project_name || p.sfprojectsid, value: p.id }))}
            />
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" style={{ "--accent": accentColor }} onClick={handleEdit} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
          </div>
        </Modal>
      )}

      {modal === "delete" && (
        <Modal title="Delete Client" onClose={closeModal}>
          <p className="delete-msg">
            Are you sure you want to delete client <strong>{current.client_id}</strong> — {current.client_name}? This action cannot be undone.
          </p>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-danger" onClick={handleDelete} disabled={saving}>{saving ? "Deleting…" : "Delete"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   SHIFT MASTER PANEL  (shift_code, shift_name, start_time, end_time + AM/PM)
══════════════════════════════════════════════ */
function TimePicker({ label, value, onChange }) {
  // value is stored as "HH:MM" in 24h; UI shows 12h with AM/PM toggle
  const parseTime = (v) => {
    if (!v) return { h: "", m: "", ampm: "AM" };
    const [hh, mm] = v.split(":");
    let h = parseInt(hh, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return { h: String(h).padStart(2, "0"), m: mm || "00", ampm };
  };

  const toValue = (h, m, ampm) => {
    let hh = parseInt(h, 10) || 0;
    if (ampm === "PM" && hh !== 12) hh += 12;
    if (ampm === "AM" && hh === 12) hh = 0;
    return `${String(hh).padStart(2, "0")}:${m || "00"}`;
  };

  const { h, m, ampm } = parseTime(value);

  const setH = (v) => onChange(toValue(v, m, ampm));
  const setM = (v) => onChange(toValue(h, v, ampm));
  const toggleAmpm = () => onChange(toValue(h, m, ampm === "AM" ? "PM" : "AM"));

  return (
    <div className="form-group">
      <label className="form-label">{label} <span style={{ color: "#ef4444" }}>*</span></label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          className="form-input"
          style={{ width: 70, textAlign: "center" }}
          type="number" min={1} max={12}
          placeholder="HH"
          value={h}
          onChange={(e) => setH(e.target.value)}
        />
        <span style={{ fontWeight: 700, fontSize: 18, color: "#64748b" }}>:</span>
        <input
          className="form-input"
          style={{ width: 70, textAlign: "center" }}
          type="number" min={0} max={59}
          placeholder="MM"
          value={m}
          onChange={(e) => setM(String(e.target.value).padStart(2, "0"))}
        />
        <button
          type="button"
          onClick={toggleAmpm}
          style={{
            padding: "6px 14px",
            borderRadius: 8,
            border: "1.5px solid #4F6EF7",
            background: ampm === "AM" ? "#4F6EF7" : "#fff",
            color: ampm === "AM" ? "#fff" : "#4F6EF7",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            transition: "all 0.2s",
            userSelect: "none",
          }}
        >
          AM
        </button>
        <button
          type="button"
          onClick={toggleAmpm}
          style={{
            padding: "6px 14px",
            borderRadius: 8,
            border: "1.5px solid #4F6EF7",
            background: ampm === "PM" ? "#4F6EF7" : "#fff",
            color: ampm === "PM" ? "#fff" : "#4F6EF7",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            transition: "all 0.2s",
            userSelect: "none",
          }}
        >
          PM
        </button>
      </div>
    </div>
  );
}

function fmt12(time24) {
  if (!time24) return "—";
  const [hh, mm] = time24.split(":");
  let h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${mm} ${ampm}`;
}

function ShiftMasterPanel({ accentColor }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { shift_code: "", shift_name: "", start_time: "09:00", end_time: "18:00" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/shift-master");
      setRows(res.data.data || []);
    } catch { toast.error("Failed to load shifts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(
    (r) =>
      r.shift_code?.toLowerCase().includes(search.toLowerCase()) ||
      r.shift_name?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setForm(emptyForm); setModal("create"); };
  const openEdit = (row) => {
    setCurrent(row);
    setForm({
      shift_code: row.shift_code,
      shift_name: row.shift_name,
      start_time: row.start_time?.slice(0, 5) || "09:00",
      end_time: row.end_time?.slice(0, 5) || "18:00",
    });
    setModal("edit");
  };
  const openDelete = (row) => { setCurrent(row); setModal("delete"); };
  const closeModal = () => { setModal(null); setCurrent(null); };

  const handleCreate = async () => {
    if (!form.shift_code.trim()) return toast.error("Shift Code is required");
    if (!form.shift_name.trim()) return toast.error("Shift Name is required");
    if (!form.start_time) return toast.error("Start Time is required");
    if (!form.end_time) return toast.error("End Time is required");
    setSaving(true);
    try {
      await api.post("/api/shift-master", form);
      toast.success("Shift created successfully");
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to create"); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!form.shift_name.trim()) return toast.error("Shift Name is required");
    if (!form.start_time) return toast.error("Start Time is required");
    if (!form.end_time) return toast.error("End Time is required");
    setSaving(true);
    try {
      await api.put(`/api/shift-master/${current.id}`, {
        shift_name: form.shift_name,
        start_time: form.start_time,
        end_time: form.end_time,
      });
      toast.success("Shift updated successfully");
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/shift-master/${current.id}`);
      toast.success("Shift deleted");
      load(); closeModal();
    } catch (e) { toast.error(e.response?.data?.message || "Failed to delete"); }
    finally { setSaving(false); }
  };

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <div className="panel-header-left">
          <div className="panel-title">Shift</div>
          <div className="panel-sub">Manage and configure shift timings</div>
        </div>
        <div className="panel-header-right">
          <div className="search-wrap">
            <FiSearch size={15} color="#94a3b8" />
            <input className="search-input" placeholder="Search by name, code..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn-add-primary" style={{ "--acc": accentColor }} onClick={openCreate}>
            <FiPlus size={15} /> Add Shift
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty-msg">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-msg">No shifts found. Click <strong>Add Shift</strong> to get started.</div>
        ) : (
          <table className="master-table">
            <thead>
              <tr>
                <th>Shift Code</th>
                <th>Shift Name</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td><span className="id-badge" style={{ "--accent": accentColor }}>{row.shift_code}</span></td>
                  <td className="name-cell">{row.shift_name}</td>
                  <td style={{ color: "#475569", fontWeight: 500 }}>{fmt12(row.start_time)}</td>
                  <td style={{ color: "#475569", fontWeight: 500 }}>{fmt12(row.end_time)}</td>
                  <td>
                    <div className="action-btns">
                      <button className="btn-icon edit" title="Edit" onClick={() => openEdit(row)}><FiEdit2 size={13} /></button>
                      <button className="btn-icon del" title="Delete" onClick={() => openDelete(row)}><FiTrash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CREATE modal */}
      {modal === "create" && (
        <Modal title="Add Shift" onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">Shift Code <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="form-input" placeholder="e.g. SHIFT001" value={form.shift_code} onChange={(e) => setForm({ ...form, shift_code: e.target.value })} />
            <span className="form-hint">Auto-converted to UPPERCASE</span>
          </div>
          <div className="form-group">
            <label className="form-label">Shift Name <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="form-input" placeholder="e.g. Morning Shift" value={form.shift_name} onChange={(e) => setForm({ ...form, shift_name: e.target.value })} />
          </div>
          <TimePicker label="Start Time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} />
          <TimePicker label="End Time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} />
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" style={{ "--accent": accentColor }} onClick={handleCreate} disabled={saving}>{saving ? "Creating…" : "Create"}</button>
          </div>
        </Modal>
      )}

      {/* EDIT modal */}
      {modal === "edit" && (
        <Modal title="Edit Shift" onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">Shift Code</label>
            <input className="form-input" value={form.shift_code} disabled style={{ opacity: 0.5 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Shift Name <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="form-input" placeholder="e.g. Morning Shift" value={form.shift_name} onChange={(e) => setForm({ ...form, shift_name: e.target.value })} />
          </div>
          <TimePicker label="Start Time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} />
          <TimePicker label="End Time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} />
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" style={{ "--accent": accentColor }} onClick={handleEdit} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
          </div>
        </Modal>
      )}

      {/* DELETE modal */}
      {modal === "delete" && (
        <Modal title="Delete Shift" onClose={closeModal}>
          <p className="delete-msg">
            Are you sure you want to delete shift <strong>{current.shift_code}</strong> — {current.shift_name}? This action cannot be undone.
          </p>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-danger" onClick={handleDelete} disabled={saving}>{saving ? "Deleting…" : "Delete"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   TAB CONFIG
══════════════════════════════════════════════ */
const TABS = [
  {
    key: "department",
    label: "Department",
    color: "#4F6EF7",
    component: () => (
      <MasterPanel
        apiPath="/api/department-master"
        idField="department_id"
        nameField="department_name"
        label="Department"
        accentColor="#4F6EF7"
        idPlaceholder="e.g. DEPT001"
        namePlaceholder="e.g. Engineering"
      />
    ),
  },
  {
    key: "designation",
    label: "Designation",
    color: "#4F6EF7",
    component: () => (
      <MasterPanel
        apiPath="/api/designation-master"
        idField="designation_id"
        nameField="designation_name"
        label="Designation"
        accentColor="#4F6EF7"
        idPlaceholder="e.g. MGR"
        namePlaceholder="e.g. Senior Manager"
      />
    ),
  },
  {
    key: "client",
    label: "Client",
    color: "#4F6EF7",
    component: () => <ClientMasterPanel accentColor="#4F6EF7" />,
  },
  {
    key: "project-status",
    label: "Project Status",
    color: "#4F6EF7",
    component: () => (
      <MasterPanel
        apiPath="/api/project-status-master"
        idField="status_code"
        nameField="status_name"
        label="Project Status"
        accentColor="#4F6EF7"
        idPlaceholder="e.g. IN_PROGRESS"
        namePlaceholder="e.g. In Progress"
      />
    ),
  },
  {
    key: "priority",
    label: "Priority",
    color: "#4F6EF7",
    component: () => (
      <MasterPanel
        apiPath="/api/priority-master"
        idField="priority_code"
        nameField="priority_name"
        label="Priority"
        accentColor="#4F6EF7"
        idPlaceholder="e.g. HIGH"
        namePlaceholder="e.g. High Priority"
      />
    ),
  },
  {
    key: "shift",
    label: "Shift",
    color: "#4F6EF7",
    component: () => <ShiftMasterPanel accentColor="#4F6EF7" />,
  },
  {
    key: "leave-policy",
    label: "Leave Policy",
    color: "#4F6EF7",
    component: () => <LeavePolicyPage isTab={true} />
  },
  {
    key: "leave-type",
    label: "Leave Type",
    color: "#4F6EF7",
    component: () => <LeaveType isTab={true} />
  },
  {
    key: "holiday-list",
    label: "Holiday List",
    color: "#4F6EF7",
    component: () => <HolidaysList isTab={true} />
  },
  {
    key: "email-settings",
    label: "Email Settings",
    color: "#4F6EF7",
    component: () => <ReportSettings isTab={true} />
  },
  {
    key: "office-locations",
    label: "Office Locations",
    color: "#4F6EF7",
    component: () => <OfficeLocations isTab={true} />
  },
  {
    key: "checkin-settings",
    label: "Check-in Reminder",
    color: "#4F6EF7",
    component: () => <CheckinSettings isTab={true} />
  }
];

/* ══════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════ */
export default function MasterPage() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("department");

  const active = TABS.find((t) => t.key === activeTab);
  const ActiveComponent = active?.component;

  return (
    <MainLayout 
      title="Master Data" 
      subtitle="Manage departments, designations, clients, statuses & priorities" 
      sidebarOpen={sidebarOpen} 
      toggleSidebar={() => setSidebarOpen((p) => !p)}
    >
      <div className="master-page">

        {/* Tab bar */}
        <div className="tab-bar-container">
          <div className="tab-bar">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`tab-btn ${activeTab === tab.key ? "tab-btn--active" : ""}`}
                style={activeTab === tab.key ? { "--tab-color": tab.color } : {}}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active panel */}
        <div className="tab-content">
          {ActiveComponent && <ActiveComponent />}
        </div>
      </div>
    </MainLayout>
  );
}